import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { useUI } from '../../store/uiStore';

interface ErrorBoundaryProps {
  children: ReactNode;
  panel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[ErrorBoundary${this.props.panel ? `: ${this.props.panel}` : ''}]`,
      error,
      info.componentStack,
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="panel"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-6)',
          }}
        >
          <AlertCircle size={36} style={{ color: 'var(--color-danger)' }} />
          <h3 className="text-xl font-bold">
            Something went wrong
          </h3>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            An unexpected error occurred in this panel.
          </p>
          <div className="flex" style={{ gap: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() =>
                useUI.getState().openFeedback(
                  'bug_report',
                  'error',
                  this.state.error?.message ?? 'Unknown error',
                )
              }
            >
              Report this error
            </button>
            <button type="button" className="btn" onClick={() => window.location.reload()}>
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
